
import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import axiosInstance from "../../Services/axiosCommon";
import DecryptData from "./Decrypt";
import NuDate from "./NuDate";
import capitalizeFirstLetter from "./Funcs";
import ViewIcon from "../Icons/ViewIcon";
import Modal from "./ModelApproval";
import ModalContainer from "../Others/ModalContainer";
import ModalDecline from "../Others/ModalDecline";
import EditIcon from "../Icons/Edit";
import {
  VerificationChecklistForm,
  VerificationChecklistHistory,
  DEFAULT_PO_CHECKLIST,
  parseChecklistMap,
  buildChecklistPayload,
  fetchChecklistByStatus,
} from "./VerificationChecklist";

const ApprovalComponent = ({
  id,
  getUrl,
  url,
  putUrl,
  statusUrl,
  refSetData,
  data,
  type,
  preUrlName,
  checklistConfig,
}) => {
  const queryClient = useQueryClient();
  const [actionDetails, setActionDetails] = useState(null);
  const [previousAction, setPreviousAction] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState("None");
  const [statusDetails, setStatusDetails] = useState([]);
  const [verifiedHistory, setVerifiedHistory] = useState(null);
  const [reviewedHistory, setReviewedHistory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState("");
  const [RequiredError, setRequiredError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [statusId, setStatusId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [seal, setSeal] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const userDetails = DecryptData("sLiAtEdReSuEdArT") || "";
  const currentUserId = Number(
    userDetails?.id || userDetails?.user_id || userDetails?.userId || 0
  );

  const currentAction = actionDetails?.action?.toLowerCase() || "";
  const currentStatus = actionDetails?.status?.toLowerCase() || "";

  const isPoReview =
    type === "PO" &&
    (currentAction === "review" || currentStatus === "reviewed" || currentStatus === "review");

  const isPoVerify =
    type === "PO" &&
    (currentAction === "verify" || currentStatus === "verified");

  // Derive active checklist config (custom prop or PO Review / PO Verify)
  const activeChecklist =
    checklistConfig !== undefined
      ? checklistConfig
      : isPoReview
        ? {
          enabled: true,
          statusKey: "review",
          targetStatus: ["review", "reviewed"],
          items: null,
          getUrl: `/PO/get-review/${id}`,
          putUrl: `/PO/update-review/${id}`,
          title: "PO Review Checklist",
          description: "Please complete the review checklist to proceed:",
        }
        : isPoVerify
          ? {
            enabled: true,
            statusKey: "verify",
            targetStatus: ["verified", "verify"],
            items: null,
            getUrl: `/PO/get-verification/${id}`,
            putUrl: `/PO/update-verification/${id}`,
            title: "PO Verification Checklist",
            description: "Please complete the verification checklist to proceed:",
          }
          : null;

  const [checklistItems, setChecklistItems] = useState([]);
  const [verificationData, setVerificationData] = useState({});
  const [submittedOptions, setSubmittedOptions] = useState({});
  const [globalTrue, setGlobalTrue] = useState("false");

  const getVerificationDetails = async (
    entityId = id,
    currentItems = checklistItems
  ) => {
    if (!entityId || !activeChecklist?.enabled) return "false";
    const itemsToUse =
      currentItems && currentItems.length > 0
        ? currentItems
        : checklistItems && checklistItems.length > 0
          ? checklistItems
          : [];

    if (itemsToUse.length === 0) {
      setVerificationData({});
      setSubmittedOptions({});
      setGlobalTrue("true");
      return "true";
    }

    const fetchUrl =
      typeof activeChecklist.getUrl === "function"
        ? activeChecklist.getUrl(entityId)
        : activeChecklist.getUrl ||
        `/${preUrlName || type}/get-verification/${entityId}`;

    try {
      const res = await axiosInstance.get(fetchUrl);
      const resData = res?.data?.data || res?.data || {};
      const checkboxs = resData.checkboxs || resData.checkboxes || {};

      const gTrue =
        resData.global_true === true ||
          String(resData.global_true).toLowerCase() === "true"
          ? "true"
          : "false";

      const { parsedData, submittedMap } = parseChecklistMap(
        checkboxs,
        itemsToUse
      );

      setVerificationData(parsedData);
      setSubmittedOptions(submittedMap);
      setGlobalTrue(gTrue);
      return gTrue;
    } catch (err) {
      console.error("Error fetching verification details:", err);
      return "false";
    }
  };

  useEffect(() => {
    let isMounted = true;
    const loadChecklist = async () => {
      if (!activeChecklist?.enabled) {
        if (isMounted) {
          setChecklistItems([]);
          setVerificationData({});
          setSubmittedOptions({});
          setGlobalTrue("false");
        }
        return;
      }

      let items = [];
      if (activeChecklist.items && activeChecklist.items.length > 0) {
        items = activeChecklist.items;
      } else {
        const queryStatus =
          activeChecklist.statusKey || (isPoReview ? "review" : "verify");
        const dynamicItems = await fetchChecklistByStatus(queryStatus);
        if (dynamicItems && Array.isArray(dynamicItems) && dynamicItems.length > 0) {
          items = dynamicItems;
        } else {
          items = [];
        }
      }

      if (isMounted) {
        setChecklistItems(items);
        if (id && items.length > 0) {
          await getVerificationDetails(id, items);
        } else {
          setVerificationData({});
          setSubmittedOptions({});
          setGlobalTrue("true");
        }
      }
    };

    loadChecklist();

    return () => {
      isMounted = false;
    };
  }, [
    id,
    activeChecklist?.enabled,
    activeChecklist?.items,
    activeChecklist?.statusKey,
    activeChecklist?.getUrl,
    isPoReview,
    isPoVerify,
  ]);

  const hasChecklistItems = Boolean(
    activeChecklist?.enabled && checklistItems && checklistItems.length > 0
  );

  const isChecklistTargetStatus = Boolean(
    activeChecklist?.enabled &&
    (activeChecklist.targetStatus
      ? Array.isArray(activeChecklist.targetStatus)
        ? activeChecklist.targetStatus.some(
          (s) =>
            actionDetails?.status?.toLowerCase() === s.toLowerCase() ||
            actionDetails?.action?.toLowerCase() === s.toLowerCase()
        )
        : actionDetails?.status?.toLowerCase() ===
        activeChecklist.targetStatus.toLowerCase() ||
        actionDetails?.action?.toLowerCase() ===
        activeChecklist.targetStatus.toLowerCase()
      : actionDetails?.status?.toLowerCase() === "verified" ||
      actionDetails?.action?.toLowerCase() === "verify" ||
      actionDetails?.action?.toLowerCase() === "review" ||
      actionDetails?.action?.toLowerCase() === "reviewed")
  );

  const isAllChecklistChecked =
    hasChecklistItems &&
    checklistItems.every(
      (item) => verificationData?.[item.id]?.status === "true"
    );

  const isAnyChecklistChecked =
    hasChecklistItems &&
    checklistItems.some(
      (item) => verificationData?.[item.id]?.status === "true"
    );

  const showVerificationChecklist = Boolean(
    hasChecklistItems &&
    isChecklistTargetStatus &&
    globalTrue !== "true"
  );

  const isChecklistPending = Boolean(
    hasChecklistItems &&
    globalTrue !== "true"
  );

  const updateVerificationDetails = async (updatedData, entityId = id) => {
    if (!entityId || !activeChecklist?.enabled || checklistItems.length === 0) return;
    const saveUrl =
      typeof activeChecklist.putUrl === "function"
        ? activeChecklist.putUrl(entityId)
        : activeChecklist.putUrl ||
        `/${preUrlName || type}/update-verification/${entityId}`;

    const payload = buildChecklistPayload(
      updatedData,
      checklistItems,
      currentUserId
    );

    try {
      const res = await axiosInstance.put(saveUrl, payload);
      return res?.data;
    } catch (err) {
      console.warn("Checklist save warning:", err);
      return null;
    }
  };

  const handleCheckboxChange = (optionKey, checked) => {
    if (submittedOptions[optionKey]) return;
    const updated = {
      ...verificationData,
      [optionKey]: {
        status: checked ? "true" : "false",
        dateTime: checked ? moment().format("DD-MM-YYYY HH:mm:ss") : "",
        userId: checked ? currentUserId : null,
      },
    };
    setVerificationData(updated);
  };

  // Function to get seal
  const getSeal = () => {
    axiosInstance
      .get("/company-seal/get-seal")
      .then((res) => {
        setSeal(res?.data?.data || []);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    getSeal();
  }, []);

  useEffect(() => {
    if (id && getUrl) {
      getActionDetails(id, getUrl);
    }
    if (id && activeChecklist?.enabled) {
      getVerificationDetails(id);
    }
  }, [id, getUrl, type, activeChecklist?.enabled]);

  // Function to get action details
  function getActionDetails(id, url) {
    axiosInstance
      .get(`${url}/${id}`)
      .then((res) => {
        console.log("Response data:", res?.data);
        setActionDetails(res?.data?.data);
        setPreviousAction(res?.data?.previous_action);
        setApprovalStatus(res?.data?.approvalStatus || "None");
      })
      .catch((err) => {
        console.error("Error fetching action details:", err);
      });
  }

  function verifyAction(id, url, status) {
    setLoading(true);
    console.log("Verifying ID:", id);
    axiosInstance
      .put(`${url}/${id}`, { status: status, action: true })
      .then((res) => {
        if (res.data.status) {
          queryClient.invalidateQueries();
          getActionDetails(id, getUrl);
          if (activeChecklist?.enabled) {
            getVerificationDetails(id);
          }
          setIsModalOpen(false);
          setLoading(false);

          type === "PR" &&
            status.toLowerCase() === "verified" &&
            refSetData({
              ...data,
              status: "Verified",
              isDraft: false,
              authorizedBy: seal?.[0]?.file,
            });
          type === "GRN" &&
            status.toLowerCase() === "verified" &&
            refSetData({
              ...data,
              status: "Verified",
              isDraft: false,
              authorizedBy: seal?.[0]?.file,
            });
          type === "DN" &&
            status.toLowerCase() === "verified" &&
            refSetData({
              ...data,
              status: "Verified",
              isDraft: false,
              authorizedBy: seal?.[0]?.file,
            });
          type === "IN" &&
            status.toLowerCase() === "verified" &&
            refSetData({
              ...data,
              status: "Verified",
              isDraft: false,
              authorizedBy: seal?.[0]?.file,
            });
          type === "PO" &&
            status.toLowerCase() === "verified" &&
            refSetData &&
            refSetData({
              ...data,
              status: "Verified",
              approvalStatus: "Verified",
              isDraft: false,
              authorizedBy: seal?.[0]?.file,
            });
          type === "PO" &&
            status.toLowerCase() === "approved" &&
            refSetData &&
            refSetData({
              ...data,
              status: "Approved",
              isDraft: false,
              authorizedBy: seal?.[0]?.file,
            });
          type === "OUT" &&
            status.toLowerCase() === "approved" &&
            refSetData({
              ...data,
              status: "Approved",
              isDraft: false,
              authorizedBy: seal?.[0]?.file,
            });
          type === "CQ" &&
            status.toLowerCase() === "approved" &&
            refSetData({
              ...data,
              status: "Approved",
              isDraft: false,
              authorizedBy: seal?.[0]?.file,
            });
          type === "Payment-Request" &&
            status.toLowerCase() === "approved" &&
            refSetData({
              ...data,
              status: "Approved",
              isDraft: false,
              authorizedBy: seal?.[0]?.file,
            });
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error("Error verifying action:", err);
      });
  }

  const [isDecline, setIsDecline] = useState(false);
  function declineAction(id, url, status, reason) {
    setLoading(true);
    if (!reason) {
      setRequiredError(true);
      return;
    }
    console.log("Decline action payload:", {
      status: status,
      action: false,
      reason,
    });

    axiosInstance
      .put(`${url}/${id}`, { status: status, action: false, reason })
      .then((res) => {
        console.log("Response from decline API:", res.data);
        if (res.data.status) {
          queryClient.invalidateQueries();
          getActionDetails(id, getUrl);
          setIsDecline(false);
          setReason("");
          setLoading(false)

          status.toLowerCase() === "verified" &&
            refSetData({
              ...data,
              status: "Declined",
              isDraft: true,
            });
        }
      })
      .catch((err) => {
        setLoading(false)
        console.error("Error declining action:", err);
      });
  }

  function getAllStatusDetails(id, url) {
    axiosInstance
      .get(`${url}/${id}`)
      .then((res) => {
        console.log("All Status Data:", res?.data);
        setStatusDetails(res?.data?.data || []);
        setVerifiedHistory(res?.data?.isVerified || res?.data?.data?.isVerified || null);
        setReviewedHistory(res?.data?.isReviewed || res?.data?.data?.isReviewed || null);
        setShowModal(true);
      })
      .catch((err) => {
        console.error("Error fetching all status details:", err);
      });

  }

  const handleEditReasonClick = (id, existingReason) => {
    setReason(existingReason);
    setStatusId(id);
    setShowEditModal(true);
    setShowModal(false);
  };

  const editReason = (id, statusId, preUrlName, reason) => {
    setLoading(true)
    if (!reason) {
      setRequiredError(true);
      return;
    }
    console.log("type", url);
    axiosInstance
      .put(`${preUrlName}/edit-reason/${statusId}`, { reason })
      .then((res) => {
        if (res.data.status) {
          queryClient.invalidateQueries();
          getAllStatusDetails(id, statusUrl);
          setShowEditModal(false);
          setLoading(false)
          // setReason("");
        }
      })
      .catch((err) => {
        setLoading(false)
        console.error("Error editing reason:", err);
      });
  };

  return (
    <div className="border border-gray rounded-lg">
      <div className="flex justify-between bg-baseColor-primary p-2 rounded-t-lg">
        <p className="text-xs 2xl:text-sm text-white font-semibold">Approval</p>
        <div
          className="cursor-pointer"
          onClick={() => {
            getAllStatusDetails(id, statusUrl);
            setShowModal(true);
          }}
        >
          <ViewIcon color="white" />
        </div>
      </div>
      <div className="p-2">
        <p className="text-xs text-[#111537]">
          {" "}
          Status :{" "}
          {approvalStatus ? capitalizeFirstLetter(approvalStatus) : "None"}{" "}
        </p>
        {previousAction && (
          <div className="mt-2 items-center">
            <p className="text-[12px] text-[#111537]">
              {previousAction?.action
                ? capitalizeFirstLetter(previousAction.status)
                : "Declined"}{" "}
              by{" "}
              {previousAction?.actionByName
                ? capitalizeFirstLetter(previousAction.actionByName)
                : "N/A"}
            </p>
            <p className="text-[10px] text-[#606279]">
              {previousAction?.dateTime ? (
                <NuDate
                  value={previousAction.dateTime}
                  format="DD-MMM-YY hh:mm a"
                />
              ) : (
                "N/A"
              )}
            </p>
          </div>
        )}

        {actionDetails && (
          <div className="flex gap-4 mt-2 justify-around">
            <p
              style={{
                backgroundColor: isChecklistPending
                  ? "#94a3b8"
                  : actionDetails?.color || "#0f766e",
              }}
              className={`px-2 py-1 hover:cursor-pointer text-white items-center rounded-md text-xs h-7 transition-all ${isChecklistPending
                ? "bg-slate-400 hover:bg-slate-500 opacity-80"
                : ""
                }`}
              title={
                isChecklistPending
                  ? "Checklist incomplete. Click to complete checklist."
                  : actionDetails?.action
              }
              onClick={async () => {
                if (hasChecklistItems) {
                  await getVerificationDetails(id, checklistItems);
                }
                setIsModalOpen(true);
              }}
            >
              {actionDetails.action}
            </p>

            {actionDetails.status !== "resubmited" && (
              <p
                className="px-2 py-1 bg-red-500 hover:cursor-pointer text-white items-center rounded-md text-xs h-7"
                onClick={() => {
                  setIsDecline(true);
                }}
              >
                Decline
              </p>
            )}
          </div>
        )}
        {/* </div> */}

        <Modal show={showModal} onClose={() => setShowModal(false)}>
          {loading ? (
            <div className="w-full text-center text-[#93949f] text-xs">
              Loading status details...
            </div>
          ) : statusDetails.length > 0 ? (
            <>
              {statusDetails.map((status, index) => {
                const isStatusVerified =
                  status.status?.toLowerCase() === "verified";
                const isStatusReviewed =
                  status.status?.toLowerCase() === "reviewed" ||
                  status.status?.toLowerCase() === "review";

                return (
                  <div
                    key={index}
                    className="mt-2 pb-2 border-b border-gray-100 last:border-b-0"
                  >
                    <p className="text-[12px] text-[#111537] font-medium">
                      {status.action
                        ? capitalizeFirstLetter(status.status)
                        : "Declined"}{" "}
                      by{" "}
                      {status.actionByName
                        ? capitalizeFirstLetter(status.actionByName)
                        : "N/A"}
                    </p>
                    {status.reason && (
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[12px] text-red-500">
                          Reason: {status.reason}
                        </p>
                        <div
                          className="cursor-pointer"
                          onClick={() => {
                            handleEditReasonClick(status.id, status.reason);
                            setShowEditModal(true);
                          }}
                        >
                          <EditIcon
                            color="red"
                            width="12"
                            height="12"
                          />
                        </div>
                      </div>
                    )}
                    <p className="text-[10px] text-[#606279]">
                      {status.dateTime ? (
                        <NuDate
                          value={status.dateTime}
                          format="DD-MMM-YY hh:mm a"
                        />
                      ) : (
                        "N/A"
                      )}
                    </p>

                    {isStatusReviewed && reviewedHistory?.checkboxs && (
                      <VerificationChecklistHistory
                        items={checklistItems}
                        checkboxs={reviewedHistory.checkboxs}
                        title="Review Checklist:"
                      />
                    )}

                    {isStatusVerified && verifiedHistory?.checkboxs && (
                      <VerificationChecklistHistory
                        items={checklistItems}
                        checkboxs={verifiedHistory.checkboxs}
                        title="Verification Checklist:"
                      />
                    )}
                  </div>
                );
              })}

              {!statusDetails.some(
                (s) => s.status?.toLowerCase() === "verified"
              ) &&
                verifiedHistory?.checkboxs && (
                  <VerificationChecklistHistory
                    items={checklistItems}
                    checkboxs={verifiedHistory.checkboxs}
                    title="Verification Checklist Progress:"
                  />
                )}
            </>
          ) : (
            <div className="w-full text-center  items-center text-[#93949f] text-xs">
              No Status Details Available.
            </div>
            //<p className='text-xs text-gray-400 flex items-center justify-center h-full'></p>
          )}
        </Modal>
      </div>
      <ModalContainer
        visiable={isModalOpen}
        hasContent={true}
        title={
          showVerificationChecklist
            ? activeChecklist?.title || "Verification Checklist"
            : "Alert"
        }
        okText={
          showVerificationChecklist
            ? "Submit"
            : actionDetails?.action || "OK"
        }
        disableOkButton={
          showVerificationChecklist ? !isAnyChecklistChecked : false
        }
        content={
          showVerificationChecklist ? (
            <VerificationChecklistForm
              items={checklistItems}
              verificationData={verificationData}
              submittedOptions={submittedOptions}
              onChange={handleCheckboxChange}
              description={activeChecklist?.description}
            />
          ) : (
            <div>
              <p>
                Are You Sure!You want to{" "}
                <span className="font-semibold">{actionDetails?.action}</span>?
              </p>
            </div>
          )
        }
        handleClick={async () => {
          if (showVerificationChecklist && hasChecklistItems) {
            if (!isAnyChecklistChecked) return;
            setLoading(true);
            try {
              const res = await updateVerificationDetails(verificationData, id);
              const isResponseGlobalTrue =
                res?.data?.global_true === true ||
                String(res?.data?.global_true).toLowerCase() === "true" ||
                res?.global_true === true ||
                String(res?.global_true).toLowerCase() === "true";

              const gTrue = isResponseGlobalTrue ? "true" : "false";
              setGlobalTrue(gTrue);

              const newSubmitted = {};
              checklistItems.forEach((item) => {
                newSubmitted[item.id] =
                  verificationData?.[item.id]?.status === "true";
              });
              setSubmittedOptions(newSubmitted);
              setIsModalOpen(false);
              await getVerificationDetails(id, checklistItems);
            } catch (err) {
              console.error("Error updating verification checklist:", err);
            } finally {
              setLoading(false);
            }
            return;
          }

          // If checklist is active and has items but globalTrue is not true, prevent edit-status
          if (hasChecklistItems && globalTrue !== "true") {
            await getVerificationDetails(id, checklistItems);
            return;
          }

          verifyAction(id, putUrl, actionDetails.status);
        }}
        closeModal={() => {
          setIsModalOpen(false);
        }}
        isLoading={loading}
      />

      <ModalDecline
        visiable={showEditModal}
        onClose={() => setShowEditModal(false)}
        content={
          <div className="p-2">
            <p className="text-sm font-semibold">Edit Reason</p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full h-24 border border-gray-300 p-2 mt-2 rounded-md"
            />
            {RequiredError && !reason && (
              <p className="text-red-500 text-sm">Reason is required</p>
            )}
          </div>
        }
        handleClick={() => editReason(id, statusId, preUrlName, reason)}
        closeModal={() => setShowEditModal(false)}
        isLoading={loading}
      // <div className="flex justify-end gap-4 mt-4">
      //   <button
      //     onClick={() => {
      //       editReason(statusId, statusUrl, reason);
      //       setShowEditModal(false);
      //     }}
      //     className="px-4 py-2 bg-blue-500 text-white rounded-md"
      //   >
      //     Submit
      //   </button>
      //   <button
      //     onClick={() => setShowEditModal(false)}
      //     className="px-4 py-2 bg-gray-500 text-white rounded-md"
      //   >
      //     Cancel
      //   </button>
      // </div>
      // </div>
      />

      <ModalDecline
        visiable={isDecline}
        title="Decline Action"
        content={
          <div>
            <p className="m-1">
              Are you sure you want to{" "}
              <span className="font-semibold">Decline</span>?
            </p>

            <label className="font-semibold">
              Reason :<span className="text-red-500">*</span>{" "}
            </label>
            <textarea
              className=" w-full p-2 border rounded-md"
              placeholder="Please provide a reason"
              value={reason}
              isRequired={true}
              onChange={(e) => setReason(e.target.value)}
            />
            {RequiredError && (
              <div className="text-red-500 text-xs mt-1">
                Reason is required
              </div>
            )}
          </div>
        }
        handleClick={() =>
          declineAction(id, putUrl, actionDetails.status, reason)
        }
        closeModal={() => setIsDecline(false)}
        disableOkButton={!reason}
        isLoading={loading}
      />
    </div>
  );
};

export default ApprovalComponent;
